import type { StripeCardNumberElementOptions } from "@stripe/stripe-js";
import type { ControlValueTextStyle } from "@freightsimple/ui-core/Controls/useControlValueTextStyle";
import type { ResolvedTheme } from "@freightsimple/ui-core/Theme/useTheme";

/**
 * Stripe elements render their own text inside an iframe, so they can't take
 * Tailwind classes — we hand Stripe an inline `style` that mirrors ui-core's
 * input text for the active presentation (boxed: text-sm / font-medium;
 * underlined: the large bold Cargo value), with a faint placeholder either way.
 * Values are read from the live theme tokens so the field text matches the
 * surrounding ui-core inputs. The chrome itself is drawn by the `FieldShell`
 * wrapper, not here.
 *
 * `resolvedTheme` is not used to pick the colours — the tokens already carry the
 * theme. It is here to make the theme a render dependency: nothing else in these
 * fields changes when the theme flips, so without it the options were built once
 * and a mid-checkout switch left light text on a dark field. It also selects the
 * right fallback for the SSR / detached case.
 */

function tokenColor(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

export function creditCardsStripeFieldOptions(
  resolvedTheme: ResolvedTheme,
  valueText: ControlValueTextStyle,
): StripeCardNumberElementOptions {
  const isDark = resolvedTheme === "dark";
  const foreground = tokenColor(
    "--color-foreground",
    isDark ? "#e4e4e7" : "#212838",
  );
  const faint = tokenColor(
    "--color-content-faint",
    isDark ? "#52525b" : "#c4c4c4",
  );
  return {
    style: {
      base: {
        fontSize: valueText.fontSize,
        lineHeight: valueText.lineHeight,
        fontWeight: valueText.fontWeight,
        color: foreground,
        "::placeholder": { color: faint, fontWeight: "400" },
      },
      invalid: {
        // The field border already turns red via FieldShell + FormItem; keep
        // the text itself readable rather than recolouring it.
        color: foreground,
      },
    },
  };
}
